import { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";

const IPAD_10TH_GEN_WIDTH = 1180;
const IPAD_10TH_GEN_HEIGHT = 820;
const IPAD_10TH_GEN_PIXEL_RATIO = 2;

// Extract iPad detection into a separate function
const detectIPad10thGen = (): boolean => {
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const pixelRatio = window.devicePixelRatio;
  const userAgent = navigator.userAgent;

  const isIPad =
    /iPad/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);

  if (isIPad && pixelRatio === IPAD_10TH_GEN_PIXEL_RATIO) {
    const isCorrectResolution =
      (screenWidth === IPAD_10TH_GEN_WIDTH &&
        screenHeight === IPAD_10TH_GEN_HEIGHT) ||
      (screenWidth === IPAD_10TH_GEN_HEIGHT &&
        screenHeight === IPAD_10TH_GEN_WIDTH);
    return isCorrectResolution;
  }

  return false;
};

const useModalPosition = (
  MODAL_HEIGHT: number,
  SAFE_TOP_PERCENTAGE: number,
  stAmtModalRef: React.RefObject<HTMLDivElement | null>
) => {
  const [viewportHeight, setViewportHeight] = useState(0);
  const [initStAmtModalTop, setInitStAmtModalTop] = useState(0);
  const [currentStAmtModalTop, setCurrentStAmtModalTop] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldCalculate, setShouldCalculate] = useState(false);

  // Added to fix bug where keyboard obscures calendar on iPad 10th Gen
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const initialViewportHeight = useRef(0);
  const isIPad10thGen = useRef(false);
  const VISIBLE_CONTENT_ANCHOR = 0.35;

  useEffect(() => {
    // Call the detection function instead of inline logic
    isIPad10thGen.current = detectIPad10thGen();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      // Use visualViewport for better keyboard detection on iOS
      const currentHeight = window.visualViewport?.height ?? window.innerHeight;

      // Track keyboard state for iPad fix
      if (isModalOpen && initialViewportHeight.current === 0) {
        initialViewportHeight.current = currentHeight;
      }

      if (initialViewportHeight.current) {
        const diff = initialViewportHeight.current - currentHeight;
        setKeyboardHeight(Math.max(diff, 0));
      }

      setViewportHeight(currentHeight);
    };

    // Use visualViewport for better iOS keyboard detection
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener("resize", handleResize);
    } else {
      window.addEventListener("resize", handleResize);
    }

    handleResize(); // Set initial value on mount

    return () => {
      if (viewport) {
        viewport.removeEventListener("resize", handleResize);
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [isModalOpen]);

  // Reset keyboard state when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      initialViewportHeight.current = 0;
      setKeyboardHeight(0);
    }
  }, [isModalOpen]);

  const additionalTop = useMemo(() => {
    if (shouldCalculate && viewportHeight / 2 < MODAL_HEIGHT) {
      const val =
        ((viewportHeight / 2 - MODAL_HEIGHT) / viewportHeight) * 100 * -1;
      return Math.min(val, SAFE_TOP_PERCENTAGE);
    }
    return 0;
  }, [shouldCalculate, viewportHeight, MODAL_HEIGHT, SAFE_TOP_PERCENTAGE]);

  useEffect(() => {
    if (!initStAmtModalTop && isModalOpen && stAmtModalRef.current) {
      setInitStAmtModalTop(stAmtModalRef.current.getBoundingClientRect().top);
    }
  }, [stAmtModalRef, initStAmtModalTop, isModalOpen]);

  useLayoutEffect(() => {
    if (shouldCalculate && stAmtModalRef?.current) {
      // calculate st amt modal top position
      setCurrentStAmtModalTop(
        initStAmtModalTop < stAmtModalRef.current.getBoundingClientRect().top
          ? initStAmtModalTop
          : 0
      );
    } else {
      setCurrentStAmtModalTop(0);
    }
  }, [additionalTop, stAmtModalRef, initStAmtModalTop, shouldCalculate]);

  const modalStyles = useMemo(() => {
    // Special handling for iPad 10th Gen when keyboard is open
    if (keyboardHeight > 0 && isIPad10thGen.current) {
      const targetPositionInVisible = viewportHeight * VISIBLE_CONTENT_ANCHOR;
      const originalCenter = initialViewportHeight.current / 2;
      const shiftNeeded = originalCenter - targetPositionInVisible;
      const shiftPercentage =
        (shiftNeeded / initialViewportHeight.current) * 100;
      const newTopPercentage = 50 - shiftPercentage;

      const minTopPercentage = 2;
      const maxTopPercentage = 80;
      const finalTopPercentage = Math.min(
        Math.max(minTopPercentage, newTopPercentage),
        maxTopPercentage
      );

      return {
        top: `${finalTopPercentage}%`,
        left: "50%",
        transform: "translate(-50%, -50%)",
        transition: "top 0.3s ease-out",
      };
    }

    const hasAbsoluteTop = Boolean(additionalTop && currentStAmtModalTop);
    const top = hasAbsoluteTop
      ? `${currentStAmtModalTop}px`
      : `${50 - (additionalTop ?? 0)}%`;

    const transform = `translate(-50%, -${hasAbsoluteTop ? 0 : 50}%)`;
    return {
      top,
      left: "50%",
      transform,
    };
  }, [additionalTop, currentStAmtModalTop, keyboardHeight, viewportHeight]);

  return { modalStyles, setIsModalOpen, setShouldCalculate };
};

export default useModalPosition;
